// Platform storage abstraction shared by the web app and the React Native app.
//
// The interface is intentionally synchronous so existing web call sites need no
// changes:
//   - Web: auto-detects `localStorage` (zero setup).
//   - React Native: call `setStorageImplementation()` once at app start with an
//     AsyncStorage-backed adapter that keeps a synchronous in-memory mirror.

export interface StorageAdapter {
  getItem(key: string): string | null;
  // Web's localStorage adapter returns void (synchronous); React Native's
  // AsyncStorage-backed adapter returns the underlying write's promise so a
  // caller that needs the write durable (the offline outbox) can await it.
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

// Minimal shape of the Web Storage API we rely on, declared locally so this
// module carries no hard DOM dependency for React Native consumers.
interface WebStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const memoryStore = new Map<string, string>();

const memoryAdapter: StorageAdapter = {
  getItem: (key) => (memoryStore.has(key) ? (memoryStore.get(key) as string) : null),
  setItem: (key, value) => {
    memoryStore.set(key, value);
  },
  removeItem: (key) => {
    memoryStore.delete(key);
  },
};

function detectDefaultAdapter(): StorageAdapter {
  try {
    const globalStorage = (globalThis as { localStorage?: WebStorageLike }).localStorage;
    if (globalStorage) {
      return {
        getItem: (key) => globalStorage.getItem(key),
        setItem: (key, value) => globalStorage.setItem(key, value),
        removeItem: (key) => globalStorage.removeItem(key),
      };
    }
  } catch {
    // Accessing localStorage can throw (Safari private mode, sandboxed iframes,
    // React Native). Fall back to an in-memory store.
  }
  return memoryAdapter;
}

let activeAdapter: StorageAdapter = detectDefaultAdapter();

/** Override the storage backend. React Native injects AsyncStorage at boot. */
export function setStorageImplementation(adapter: StorageAdapter): void {
  activeAdapter = adapter;
}

/**
 * Stable storage handle that always delegates to the active adapter.
 *
 * setItem/removeItem return the adapter's own promise (an `async` wrapper
 * turns web's synchronous throw into a rejection the same as mobile's
 * AsyncStorage rejection) rather than swallowing it, so a caller that awaits
 * the write — the mobile offline outbox — learns when it did not reach disk.
 * getItem stays synchronous and still swallows: it always has a safe `null`
 * fallback, and every caller reads it synchronously.
 */
export const storage: {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
} = {
  getItem: (key) => {
    try {
      return activeAdapter.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: async (key, value) => {
    await activeAdapter.setItem(key, value);
  },
  removeItem: async (key) => {
    await activeAdapter.removeItem(key);
  },
};
